import React from 'react';
import CrownIcon from '@mui/icons-material/EmojiEvents';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { Box, Button, Chip, CircularProgress, FormControl, Grid, MenuItem, Paper, Select, SelectChangeEvent, Typography } from '@mui/material';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { styled } from '@mui/material/styles';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import CircleIcon from '@mui/icons-material/Circle';
import api from '../../services/api/axiosConfig';
import { bookingService, ExtendStayRequest } from '../../services/api/bookingService';


const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: '#1a90ff',
    ...theme.applyStyles('dark', {
      backgroundColor: '#308fe8',
    }),
  },
}));


const MyStayContent = () => {

  const [selectedSection, setSelectedSection] = useState("My Stays");
  const [openToast, setOpenToast] = useState(false);
  const [progress, setProgress] = useState(100);
  const [toastMessage, setToastMessage] = useState("");
  const [bookings, setBookings] = useState<any[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [currentBookingStatus, setCurrentBookingStatus] = useState<string>("");
  const [selectedExtension, setSelectedExtension] = useState<'4hours' | '8hours' | 'fullday' | ''>('');
  const [extendStayLoading, setExtendStayLoading] = useState(false);
  const [insuranceLoading, setInsuranceLoading] = useState(false);

  const [currentDate, setCurrentDate] = useState(new Date());
  const dummyValues: Record<string, string> = {
    "2025-10-13": "Past Stay",
    "2025-11-03": "Past Stay",
    "2025-11-21": "Active Stay",
    "2025-11-26": "Upcoming Stay",
  };
  // 👉 Put this function at the top of your component
  const getBgColor = (dateString: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
  
    const d = new Date(dateString);
    d.setHours(0, 0, 0, 0);
  
    // Today
    if (d.getTime() === today.getTime()) {
      return "rgba(5,150,105,0.4)"; 
    }
  
    // Past
    if (d.getTime() < today.getTime()) {
      return "rgba(209,213,219,0.4)"; 
    }
  
    // Future
    return "rgba(91,141,255,0.4)";
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayIndex = new Date(year, month, 1).getDay();
  const monthNames = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December"
  ];
  
  const styles: any = {
    container: {
      width: "100%",
      background: "#1F262C",
      padding: "20px",
      borderRadius: "12px",
      color: "white",
      fontFamily: "sans-serif",
      marginBottom:"30px"
    },
    headerBar: {
      display: "flex",
      // justifyContent: "space-between",
      alignItems: "center",
      padding: "10px 0",
      marginBottom: "10px"
    },
    navBtn: {
      padding: "6px 12px",
      background: "#2C353F",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      color: "white",
      marginRight: "10px",
      marginLeft: "10px",
    },
    calendarGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      // gap: "6px",
      width: "100%"
    },
    headerCell: {
      textAlign: "center",
      fontWeight: "bold",
      padding: "10px 0",
      background: "#273645"
    },
    dayCell: {
      minHeight: "70px",
      // background: "#1F262C",
      // borderRadius: "8px",
      // border: "1px solid #3D546B",
      padding: "8px",
      cursor: "default",
      fontSize: "14px"
    },
    dayClickable: {
      // background: "#3776FF",
      cursor: "pointer",
      color: "white"
    },
    valueLabel: {
      marginTop: "6px",
      fontSize: "11px",
      fontWeight: "bold",
      display: "block"
    }
  };
  const goToNextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToPrevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const goToNextYear = () => setCurrentDate(new Date(year + 1, month, 1));
  const goToPrevYear = () => setCurrentDate(new Date(year - 1, month, 1));

  const handleDateClick = (dateKey: string) => {
    if (dummyValues[dateKey]) {
      alert(`Value: ${dummyValues[dateKey]}`);
    }
  };

  const cells = [];

  // Empty starting cells
  for (let i = 0; i < firstDayIndex; i++) {
    cells.push(<div key={`empty-${i}`}></div>);
  }

  // Days with values
  for (let day = 1; day <= daysInMonth; day++) {
    const dateKey = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const hasValue = dummyValues[dateKey];
  
    // If value exists → bg with opacity
    const bgColor = hasValue ? getBgColor(dateKey) : "#1F262C";
  
    // If value exists → border-left same color as bg
    const dynamicBorderLeft = hasValue ? `4px solid ${bgColor}` : "none";
  
    cells.push(
      <div
        key={day}
        style={{
          ...styles.dayCell,
          background: bgColor,
          ...(hasValue ? styles.dayClickable : {}),
          borderLeft: dynamicBorderLeft
        }}
        onClick={() => hasValue && handleDateClick(dateKey)}
      >
        {day}
  
        {hasValue && (
          <span style={styles.valueLabel}>{dummyValues[dateKey]}</span>
        )}
      </div>
    );
  }
  

  const handleClick = () => {
    setOpenToast(true);
    setProgress(100);
  };

  const formatDateTime = (dateString: string | undefined) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
  
    return date.toLocaleString("en-US", {
      month: "short",      // Jun
      day: "numeric",      // 10
      year: "numeric",     // 2025
      hour: "numeric",     // 3
      minute: "2-digit",   // 00
      hour12: true         // PM
    });
  };

  const getExtensionPrice = (type: '4hours' | '8hours' | 'fullday') => {
    switch (type) {
      case '4hours': return 20.00;
      case '8hours': return 35.00;
      case 'fullday': return 50.00;
      default: return 0;
    }
  };

  const handleExtensionChange = (event: SelectChangeEvent) => {
    setSelectedExtension(event.target.value as '4hours' | '8hours' | 'fullday' | '');
  };

  const handleAddInsurance = async () => {
    if (!bookings || bookings.length === 0 || !bookings[0]?.id) {
      setOpenToast(true);
      setToastMessage("No booking found. Please try again.");
      return;
    }

    try {
      setInsuranceLoading(true);
      const bookingId = bookings[0].id;
      const updatedBooking = await bookingService.addInsurance(bookingId);
      
      // Update the bookings array with the updated booking
      if (bookings.length > 0) {
        const updatedBookings = [...bookings];
        updatedBookings[0] = {
          ...updatedBookings[0],
          ...updatedBooking
        };
        setBookings(updatedBookings);
      }
      
      setOpenToast(true);
      setToastMessage("Insurance added successfully!");
    } catch (err: any) {
      console.error('Failed to add insurance:', err);
      setOpenToast(true);
      
      // Handle different error scenarios
      let errorMessage = "Failed to add insurance. Please try again.";
      
      if (err.response) {
        // Server responded with error status
        const responseData = err.response.data;
        
        // Check for database/query errors
        if (responseData?.message?.includes('invalid input syntax for type numeric') || 
            responseData?.error?.includes('invalid input syntax for type numeric') ||
            responseData?.message?.includes('QueryFailedError')) {
          errorMessage = "A calculation error occurred. Please contact support or try again later.";
        } else if (err.response.status === 500) {
          errorMessage = "Server error occurred. Please try again later or contact support.";
        } else if (responseData?.message) {
          errorMessage = responseData.message;
        } else if (responseData?.error) {
          errorMessage = responseData.error;
        }
      } else if (err.message) {
        // Check error message for database errors
        if (err.message.includes('invalid input syntax for type numeric') || 
            err.message.includes('QueryFailedError')) {
          errorMessage = "A calculation error occurred. Please contact support or try again later.";
        } else {
          errorMessage = err.message;
        }
      }
      
      setToastMessage(errorMessage);
    } finally {
      setInsuranceLoading(false);
    }
  };

  const handleExtendStay = async () => {
    if (!selectedExtension) {
      handleClick();
      setToastMessage("Please select an extension option.");
      return;
    }

    if (!bookings || bookings.length === 0 || !bookings[0]?.id) {
      handleClick();
      setToastMessage("No booking found. Please try again.");
      return;
    }

    try {
      setExtendStayLoading(true);
      
      const bookingId = bookings[0].id;
      const currentBooking = bookings[0];
      const extensionPrice = getExtensionPrice(selectedExtension as '4hours' | '8hours' | 'fullday');
      
      // Calculate extended hours
      const extensionHours = selectedExtension === '4hours' ? 4 : selectedExtension === '8hours' ? 8 : 24;
      
      // Save booking details and extension info to session storage
      const extensionData = {
        bookingId: bookingId,
        booking: currentBooking,
        extensionType: selectedExtension,
        extensionHours: extensionHours,
        extensionAmount: extensionPrice,
        isExtension: true
      };
      
      sessionStorage.setItem('extensionData', JSON.stringify(extensionData));
      
      // Save booking details for payment page (similar to new booking flow)
      const bookingDetails = {
        bookingId: bookingId,
        baseRate: extensionPrice,
        insurancePrice: 0,
        overstayPrice: 0,
        taxesAndFees: 0,
        total: extensionPrice,
        isExtension: true,
        extensionType: selectedExtension,
        extensionHours: extensionHours,
        originalBooking: currentBooking
      };
      
      sessionStorage.setItem('bookingDetails', JSON.stringify(bookingDetails));
      
      setSelectedExtension('');
      setExtendStayLoading(false);
      
      // Redirect to payment details page
      navigate('/payment-details');
    } catch (error: any) {
      console.error("Error extending stay:", error);
      handleClick();
      setToastMessage(
        error.response?.data?.message || 
        error.message ||
        "Failed to extend stay. Please try again."
      );
      setExtendStayLoading(false);
    }
  };

  // Function to calculate booking status
  const getBookingStatus = (check_in: string, check_out: string, bookingStatus?: string) => {
    // If booking is cancelled, return "cancelled"
    if (bookingStatus === "cancelled") {
      return "cancelled";
    }
    if (bookingStatus === "checked out") {
      return "checked out";
    }

    const now = new Date();
    const start = new Date(check_in);
    const end = new Date(check_out);

    if (now < start) return "Upcoming";
    if (now > end) return "Completed";
    return "Ongoing"; // check_in <= now <= check_out
  };

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        setBookingsLoading(true);
        const response = await api.get("/bookings/list?includeStats=true", {
          params: {
            page: 1,
            size: 10,
          }
        });

        setBookings(response.data.data);
      } 
      catch (error) {
        console.error("Error fetching bookings:", error);
      } finally {
        setBookingsLoading(false);
      }
    };

    fetchBookings();
  }, []);

  // Calculate booking status when bookings change
  useEffect(() => {
    if (bookings.length > 0) {
      const first: any = bookings[0];
      const statusValue = getBookingStatus(first.check_in, first.check_out, first.status);
      setCurrentBookingStatus(statusValue);
    }
  }, [bookings]);

  const navigate = useNavigate();
  return (
    <>
    <Box sx={{ background: '#151A23', color: '#fff', marginTop: "56px", mb:"-80px", borderTop:"1px solid #2C353F", borderBottom:"1px solid #2C353F", padding: "17px 31px",
      "& svg":{
        fontSize: "24px",
        display: "block",
      }
     }}>
        <Typography sx={{ fontSize: 18, fontWeight: "semibold", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between" }}>{selectedSection}
        {selectedSection !== "My Stays" && (     
        <span onClick={() => setSelectedSection("My Stays")} style={{ cursor: "pointer", color: "white", fontSize: 16, fontWeight: "bold" , float: "right", border: "1px solid white", borderRadius: "4px", padding: "4px 12px"
  }}>
    <ArrowBackOutlinedIcon />
  </span>
  )}
        </Typography>
        </Box>
    <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2, md: 3 }, background: '#151A23', minHeight: '100vh', color: '#fff', marginTop: 10 }}>
      {selectedSection === "My Stays" && (
        <>
<Grid container spacing={3} sx={{ backgroundColor: '#1F262C', borderRadius: 3, p:"30px" }}>
<Grid size={{xs: 12, md: 12, lg: 12, xl: 12}} >
<Typography sx={{ pb: "10px", textAlign: 'left'}} fontSize={24} variant="subtitle1" fontWeight="bold">Current Booking Status
  {currentBookingStatus && (
    <span style={{
      background: currentBookingStatus === "cancelled" 
        ? "#E2322061" 
        : currentBookingStatus === "Completed" 
        ? "#05966961"
        : currentBookingStatus === "Ongoing"
        ? "#3776FF61"
        : "#cf952f61",
      color: currentBookingStatus === "cancelled"
        ? "#FFAEA6"
        : currentBookingStatus === "Completed"
        ? "#2CEFA7"
        : currentBookingStatus === "Ongoing"
        ? "#90caf9"
        : "#FFB135",
      fontSize: 16, 
      float: "right",  
      borderRadius: "10px", 
      padding: "4px 20px",
      fontWeight: "bold",
      textTransform: "capitalize"
    }}>
      {currentBookingStatus}
    </span>
  )}
  </Typography>
          </Grid>
          {bookingsLoading ? (
            <Grid size={{xs: 12, md: 12, lg: 12, xl: 12}}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress sx={{ color: '#3776FF' }} />
              </Box>
            </Grid>
          ) : bookings.length === 0 ? (
            <Grid size={{xs: 12, md: 12, lg: 12, xl: 12}}>
              <Paper sx={{ p: 6, mb: 3, borderRadius: 3, width: '100%', textAlign: 'center', backgroundImage: 'none !important', boxShadow: 'none !important' }} elevation={4}>
                <Typography sx={{ color: '#b0b8c1', fontSize: 18, fontWeight: 500, mb: 2 }}>
                  No Active Booking
                </Typography>
                <Typography sx={{ color: '#6A6E71', fontSize: 14, mb: 3 }}>
                  You don't have any active bookings at the moment
                </Typography>
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/')}
                  sx={{ 
                    backgroundColor: '#3776FF', 
                    color: '#fff', 
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: '#1d4ed8',
                    }
                  }}
                >
                  Browse Available Botels
                </Button>
              </Paper>
            </Grid>
          ) : (
            <>
              <Grid size={{xs: 12, md: 6, lg: 6, xl: 6}} sx={{"& .MuiPaper-root": {
            backgroundColor: "#131A21",
            boxShadow: "none",        // removes Paper shadow
            backgroundImage: "none",  }}}>
            <Paper sx={{ p:"30px", mb: 3, borderRadius: 3, width: '100%', display: 'flex', flexDirection: 'column', "& button.MuiIconButton-root" :{
              display: "none",
            }, "& .MuiInputBase-root":{
              p:0,
            }}} elevation={4}>
              <Typography fontSize={16} sx={{ mb: 1.5 }}>Check-In</Typography>
              <Typography fontSize={20} display="flex" alignItems="center" ><img
                    src="/icons/calender-icon.svg"
                    alt="calender"
                    style={{ paddingRight: "10px", display: "block" }}
                  /> <span>{formatDateTime(bookings[0]?.check_in)}</span></Typography>
                   
            </Paper>
            </Grid>
            <Grid size={{xs: 12, md: 6, lg: 6, xl: 6}} sx={{"& .MuiPaper-root": {
            backgroundColor: "#131A21",
            boxShadow: "none",        // removes Paper shadow
            backgroundImage: "none",  }}}>
            <Paper sx={{ p:"30px", mb: 3, borderRadius: 3, width: '100%', display: 'flex', flexDirection: 'column', 
              "& button.MuiIconButton-root" :{
              display: "none",
            }, "& .MuiInputBase-root":{
              p:0,
            }
            }} elevation={4}>
              <Typography fontSize={16} sx={{ mb: 1.5 }}>Check-out</Typography>
              <Typography fontSize={20} display="flex" alignItems="center" ><img
                    src="/icons/calender-icon.svg"
                    alt="calender"
                    style={{ paddingRight: "10px", display: "block" }}
                  /> <span>{formatDateTime(bookings[0]?.check_out)}</span></Typography>
           
            </Paper>
            </Grid>
            <Button variant="contained" color="primary" sx={{mt:"-20px", width:"100%", backgroundColor: "#3776FF", color: "#fff", borderRadius: 2, fontSize: 16, fontWeight: "bold", py: 1.5, textTransform: "none", "&:hover": { opacity: 0.5 } }}
            onClick={() => navigate(`/client/all-bookings`)}
            >
            <img
                      src="/icons/button-icon-white.svg"
                      alt="calendar icon"
                      style={{ width: 22, height: 22, marginRight: 10}}
              />
              View All Bookings
              </Button>
            </>
          )}
      </Grid>
      <Grid container spacing={3} sx={{mt:"20px"}}>
        <Grid size={{xs: 12, md: 6, lg: 6, xl: 6}} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, background: '#1F262C', borderRadius: 3, position: 'relative' }} elevation={4}>
        {/* Coming Soon Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            backdropFilter: 'blur(2px)',
          }}
        >
          <Typography
            sx={{
              color: '#9ca3af',
              fontSize: 24,
              fontWeight: 'bold',
              mb: 1,
            }}
          >
            Coming Soon
          </Typography>
          <Typography
            sx={{
              color: '#6b7280',
              fontSize: 16,
              textAlign: 'center',
              px: 2,
            }}
          >
            This feature will be available in a future release
          </Typography>
        </Box>
        <Grid size={{xs: 12, md: 12, lg: 12, xl: 12}} >
<Typography sx={{ pb: "10px", textAlign: 'left'}} fontSize={20} variant="subtitle1" fontWeight="bold">
  Loyalty & Rewards Progress
  <span style={{background: "#131A21", color: "white", fontSize: 16, float: "right",border: "1px solid white",  borderRadius: "8px", padding: "4px 20px"}}>
  550 points until gold
  </span>
  </Typography>

  <Box sx={{ width: "100%", mt:"30px" }}>
  {/* Top Labels */}
  <Box 
    sx={{ 
      display: "flex", 
      justifyContent: "space-between", 
      alignItems: "center",
      mb: 1
    }}
  >
    <Typography sx={{ color: "white", fontSize: 14 }}>
    Silver
    </Typography>

    <Typography sx={{ color: "#E5D262", fontSize: 14 }}>
      Gold
    </Typography>
  </Box>

  {/* Progress Bar */}
  <BorderLinearProgress variant="determinate" value={80} />
</Box>
<Box sx={{ mt: "20px", display: "flex", gap: "12px" }}>
  <Typography 
    sx={{ 
      borderRadius: "10px",
      flex: 1,                    // makes width = 50%
      color: "white", 
      fontSize: 16, 
      background: "#131A21", 
      p: "14px",
      fontWeight:"bold"
    }}
  >
    Current points 
    <span style={{color: "#3776FF", fontSize: 18, marginLeft:"10px" }}>
      2,450
    </span>
  </Typography>

  <Typography 
    sx={{ 
      borderRadius: "10px",
      flex: 1,                    // makes width = 50%
      color: "white", 
      fontSize: 16, 
      background: "#131A21", 
      p: "14px",
      fontWeight:"bold"
    }}
  >
    Next reward
    <span style={{color: "#0DA85A", fontSize: 18, marginLeft:"10px", float:"right"}}>
    $50
    </span>
  </Typography>
</Box>

          </Grid>

</Paper>
          </Grid>
          <Grid size={{xs: 12, md: 6, lg: 6, xl: 6}} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, background: '#1F262C', borderRadius: 3 }} elevation={4}>
        <Grid size={{xs: 12, md: 12, lg: 12, xl: 12}} >
<Typography sx={{ pb: "10px", textAlign: 'left'}} fontSize={24} variant="subtitle1" fontWeight="bold">
protect your stay
  <span style={{marginLeft:"10px",background: "#131A21", color: "#93B6FF", fontSize: 13, fontWeight:"bold" ,border: "1px solid #93B6FF",  borderRadius: "15px", padding: "4px 20px"}}>
  Recommended
  </span>
  <img
                src="/icons/crown-icon.svg"
                alt="calender"
                style={{ paddingRight: "10px", display: "block", float:"right" }}
              />
  </Typography>

 <Typography sx={{ fontSize: 16, fontWeight:"bold", pt:"10px" }}>
 "82% of travelers protect their luggage
 </Typography>

          </Grid>
          <Button  
            onClick={handleAddInsurance}
            disabled={insuranceLoading || bookings[0]?.payment_status === 'completed' || bookings[0]?.status === 'cancelled' || bookings[0]?.status === 'checked out' || !bookings[0]?.id}
            color="primary" 
            sx={{
              mt:"45px", 
              mb:"5px", 
              width:"100%", 
              backgroundColor: "#3776FF", 
              color: "#fff", 
              borderRadius: 2, 
              fontSize: 16, 
              fontWeight: "bold", 
              py: 1.5, 
              textTransform: "none", 
              "&:hover": { opacity: 0.5 },
              "&:disabled": { opacity: 0.5 }
            }}
          >
            {insuranceLoading ? (
              <>
                <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                Adding Insurance...
              </>
            ) : (
              "Add Insurance Now!"
            )}
          </Button>

</Paper>
          </Grid>
     </Grid>

     <Grid container spacing={3} sx={{mt:"20px"}}>
     <Grid size={{xs: 12, md: 6, lg: 6, xl: 6}} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, background: '#1F262C', borderRadius: 3 }} elevation={4}>
        <Grid size={{xs: 12, md: 12, lg: 12, xl: 12}} >
<Typography sx={{ pb: "10px", textAlign: 'left'}} fontSize={20} variant="subtitle1" fontWeight="bold">
Extend Your Stay
  </Typography>

 <Typography sx={{ fontSize: 16, fontWeight:"bold", pt:"10px", textTransform:"capitalize" }}>
 Loving your stay? Extend it with just one click!
 </Typography>

          </Grid>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <Select
              value={selectedExtension}
              onChange={handleExtensionChange}
              displayEmpty
              sx={{
                backgroundColor: '#131A21',
                color: '#fff',
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2C353F',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3776FF',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3776FF',
                },
                '& .MuiSvgIcon-root': {
                  color: '#fff',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: '#1F262C',
                    color: '#fff',
                    '& .MuiMenuItem-root': {
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: '#131A21',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#131A21',
                        '&:hover': {
                          backgroundColor: '#131A21',
                        },
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="" disabled>
                Select extension option
              </MenuItem>
              <MenuItem value="4hours">Extend +4 hours - $20</MenuItem>
              <MenuItem value="8hours">Extend +8 hours - $35</MenuItem>
              <MenuItem value="fullday">Full Day Extension - $50</MenuItem>
            </Select>
          </FormControl>
          <Button  
            onClick={handleExtendStay}
            disabled={!selectedExtension || extendStayLoading || bookings[0]?.status === 'cancelled' || bookings[0]?.status === 'checked out'}
            color="primary" 
            sx={{
              mt: "10px", 
              mb: "5px", 
              width: "100%", 
              backgroundColor: "#3776FF", 
              color: "#fff", 
              borderRadius: 2, 
              fontSize: 16, 
              fontWeight: "bold", 
              py: 1.5, 
              textTransform: "none", 
              "&:hover": { opacity: 0.5 },
              "&:disabled": { opacity: 0.5 }
            }}
          >
            {extendStayLoading ? (
              <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
            ) : (
              <img
                src="/icons/calender-icon.svg"
                alt="calender"
                style={{ paddingRight: "10px", display: "block" }}
              />
            )}
            {extendStayLoading ? "Extending..." : "Extend Now"}
          </Button>

</Paper>
          </Grid>
          <Grid size={{xs: 12, md: 6, lg: 6, xl: 6}} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Paper sx={{ p: { xs: 2, md: 3 }, pb:"82px !important", mb: 3, background: '#1F262C', borderRadius: 3, position: 'relative' }} elevation={4}>
        {/* Coming Soon Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            backdropFilter: 'blur(2px)',
          }}
        >
          <Typography
            sx={{
              color: '#9ca3af',
              fontSize: 24,
              fontWeight: 'bold',
              mb: 1,
            }}
          >
            Coming Soon
          </Typography>
          <Typography
            sx={{
              color: '#6b7280',
              fontSize: 16,
              textAlign: 'center',
              px: 2,
            }}
          >
            This feature will be available in a future release
          </Typography>
        </Box>
        <Grid size={{xs: 12, md: 12, lg: 12, xl: 12}} >
          <Typography sx={{ pb: "15px", textAlign: 'left'}} fontSize={16} variant="subtitle1" fontWeight="bold">
          Per booking<span style={{float:"right", fontSize: 14}}>$20</span>
          </Typography>
          <Typography sx={{ pb: "15px", textAlign: 'left'}} fontSize={16} variant="subtitle1" fontWeight="bold">
          Loyalty discount<span style={{float:"right", fontSize: 14, color:"#E23220"}}>-$0</span>
          </Typography>
          <hr style={{border: "1px solid #2C353F", margin:"24px 0"}} />
          <Typography sx={{ pb: "15px", textAlign: 'left'}} fontSize={16} variant="subtitle1" fontWeight="bold">
          Total<span style={{float:"right", fontSize: 14, color:"#0DA85A"}}>-$20</span>
          </Typography>
          
          </Grid>
</Paper>
          </Grid>
     </Grid>
      </>


    )} 

<Box sx={{ mt: 5, py: 2, borderTop: '1px solid #232B3E', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, color: '#b0b8c1', fontSize: 14 }}>
        <Typography sx={{ fontSize: 14 }}>© 2025 BOTEL. All rights reserved.</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button sx={{ color: '#b0b8c1', textTransform: 'none' }}>Terms</Button>
          <Button sx={{ color: '#b0b8c1', textTransform: 'none' }}>Privacy</Button>
          <Button sx={{ color: '#b0b8c1', textTransform: 'none' }}>Support</Button>
        </Box>
      </Box>
    </Box>
    </>
  );
};

export default MyStayContent; 